import { useSettings } from './common.js';
import { PANES, pathForPane, type PaneName } from './panes.js';
import { SourcesPane } from './SourcesPane.js';
import { AddPane } from './AddPane.js';
import { IngestPane } from './IngestPane.js';
import { BuyPane } from './BuyPane.js';
import { KeysPane } from './KeysPane.js';
import { PeoplePane } from './PeoplePane.js';
import { AccountPane } from './AccountPane.js';

/**
 * Configuration: a page, not an overlay. Each section has its own address
 * (/configuration/<section>, bare /configuration meaning Providers), so it can
 * be linked to, reloaded, and reached with Back — none of which a modal gave
 * us.
 *
 * The files and types here still say "settings" because that's what the server
 * calls it (/api/settings, useSettings); only the screen is named
 * Configuration. Older addresses — /settings/<pane> and the modal-era
 * #settings/<pane> and #add — are still understood and rewritten on arrival;
 * see legacyPane and its use in App.
 */
export { CONFIG_ROOT, PANES, paneFromPath, pathForPane, legacyPane, type PaneName } from './panes.js';

interface Props {
  pane: PaneName;
  navigate: (to: string) => void;
}

export function SettingsPage({ pane, navigate }: Props) {
  const settings = useSettings();
  const current = PANES.find((p) => p.name === pane)!;

  return (
    <div>
      <div className="pagehead">
        <h1>Configuration</h1>
      </div>
      <div className="settings-layout">
        <nav className="settings-side" aria-label="Configuration sections">
          {PANES.map((p) => (
            <a
              key={p.name}
              href={pathForPane(p.name)}
              aria-current={p.name === pane ? 'page' : undefined}
              onClick={(e) => {
                e.preventDefault();
                navigate(pathForPane(p.name));
              }}
            >
              {p.label}
            </a>
          ))}
        </nav>
        <section className="settings-body" aria-label={current.label}>
          <div className="settings-head">
            <h2>{current.label}</h2>
          </div>
          {settings.isLoading ? (
            <p className="kicker">Loading…</p>
          ) : settings.isError ? (
            <p className="form-err">Could not load settings. {(settings.error as Error).message}</p>
          ) : (
            (() => {
              const s = settings.data!;
              switch (pane) {
                case 'sources':
                  return <SourcesPane settings={s} />;
                case 'add':
                  return <AddPane settings={s} />;
                case 'ingest':
                  return <IngestPane settings={s} />;
                case 'buy':
                  return <BuyPane settings={s} />;
                case 'keys':
                  return <KeysPane settings={s} />;
                case 'people':
                  return <PeoplePane settings={s} />;
                case 'account':
                  return <AccountPane settings={s} />;
              }
            })()
          )}
        </section>
      </div>
    </div>
  );
}
